/**
 * Admin · Universal AI Feature Control
 * See ALL LLM-consuming features · enable/disable · set per-user quotas · view spend.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Save, Zap, TrendingUp, Users as UsersIcon } from 'lucide-react';
import api from '../../utils/api';

const CATEGORY_COLOR = {
  chat: 'from-cyan-500 to-blue-500',
  voice: 'from-purple-500 to-fuchsia-500',
  report: 'from-amber-500 to-orange-500',
  premium: 'from-red-500 to-pink-500',
  spiritual: 'from-violet-500 to-purple-500',
  business: 'from-emerald-500 to-teal-500',
  image: 'from-fuchsia-500 to-rose-500',
  utility: 'from-slate-500 to-slate-600',
};

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

export default function AIUsageAdmin() {
  const [features, setFeatures] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // {feature_id: {enabled, daily, monthly}}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, s] = await Promise.all([
        api.get('/admin/ai-control/features'),
        api.get('/admin/ai-control/summary'),
      ]);
      setFeatures(f.data.features || {});
      setSummary(s.data);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (fid) => {
    const cur = features[fid];
    const e = edits[fid] || {};
    try {
      await api.post(`/admin/ai-control/features/${fid}`, {
        enabled: e.enabled !== undefined ? e.enabled : cur.enabled,
        daily_limit_per_user: e.daily !== undefined ? Number(e.daily) : cur.daily_limit_per_user,
        monthly_limit_per_user: e.monthly !== undefined ? Number(e.monthly) : cur.monthly_limit_per_user,
      });
      toast.success(`✅ ${fid} updated`);
      setEdits((prev) => { const x = { ...prev }; delete x[fid]; return x; });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const setEdit = (fid, key, val) => setEdits((prev) => ({ ...prev, [fid]: { ...(prev[fid] || {}), [key]: val } }));

  const categoryGroups = {};
  Object.entries(features).forEach(([fid, f]) => {
    const c = f.category || 'utility';
    if (!categoryGroups[c]) categoryGroups[c] = [];
    categoryGroups[c].push([fid, f]);
  });

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-6 text-white" data-testid="ai-usage-admin">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black flex items-center gap-1"><Zap className="w-3 h-3" /> ADMIN · UNIVERSAL LLM CONTROL</div>
            <h1 className="text-2xl md:text-3xl font-black">🎛️ AI Feature Tracker &amp; Controller</h1>
            <p className="text-sm text-slate-400">All Emergent LLM Key consuming features — kill-switch + quota + spend tracker</p>
          </div>
          <button onClick={load} disabled={loading} className="px-3 py-2 rounded-lg bg-white/10 text-sm flex items-center gap-2"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
        </div>

        {/* Global summary */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-cyan-400/40 bg-cyan-500/10 p-3">
              <div className="text-[9px] uppercase tracking-widest text-cyan-300 font-black">Today · Total LLM Calls</div>
              <div className="text-3xl font-black text-white">{fmt(summary.total_today)}</div>
            </div>
            <div className="rounded-xl border border-purple-400/40 bg-purple-500/10 p-3">
              <div className="text-[9px] uppercase tracking-widest text-purple-300 font-black">Month · Total Calls</div>
              <div className="text-3xl font-black text-white">{fmt(summary.total_month)}</div>
            </div>
            <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-3">
              <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black">Month Est. Cost (USD)</div>
              <div className="text-3xl font-black text-white">${summary.month_cost_usd.toFixed(4)}</div>
            </div>
            <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-3">
              <div className="text-[9px] uppercase tracking-widest text-emerald-300 font-black">Month Est. Cost (INR)</div>
              <div className="text-3xl font-black text-white">₹{fmt(summary.month_cost_inr)}</div>
            </div>
          </div>
        )}

        {/* 💰 AI Cost Savings Tile (Trial Room pHash + downscale cache) */}
        <CostSavingsTile />

        {/* Feature list grouped by category */}
        {Object.entries(categoryGroups).map(([cat, list]) => (
          <div key={cat} className="rounded-2xl border border-white/10 bg-slate-900 p-4">
            <div className="text-[10px] uppercase tracking-widest font-black mb-3" style={{ background: 'linear-gradient(90deg,#a5f3fc,#f0abfc)', WebkitBackgroundClip: 'text', color: 'transparent' }}>
              🗂️ {cat.toUpperCase()} ({list.length})
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {list.map(([fid, f]) => {
                const e = edits[fid] || {};
                const enabled = e.enabled !== undefined ? e.enabled : f.enabled;
                const daily = e.daily !== undefined ? e.daily : f.daily_limit_per_user;
                const monthly = e.monthly !== undefined ? e.monthly : f.monthly_limit_per_user;
                const dirty = Object.keys(e).length > 0;
                return (
                  <div key={fid} data-testid={`ai-feature-${fid}`} className={`rounded-xl p-3 border-2 ${enabled ? 'border-emerald-400/30' : 'border-red-400/40'} bg-black/40`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <div className={`text-[9px] uppercase tracking-widest font-black bg-gradient-to-r ${CATEGORY_COLOR[cat] || 'from-slate-500 to-slate-600'} bg-clip-text text-transparent`}>{f.model}</div>
                        <div className="text-sm font-black text-white truncate">{f.name}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{fid}</div>
                      </div>
                      <label className="flex items-center gap-1 text-[10px] cursor-pointer">
                        <input type="checkbox" checked={enabled} onChange={(ev) => setEdit(fid, 'enabled', ev.target.checked)} data-testid={`ai-toggle-${fid}`} className="accent-emerald-500 w-4 h-4" />
                        <span className={enabled ? 'text-emerald-300 font-black' : 'text-red-300 font-black'}>{enabled ? 'ON' : 'OFF'}</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <label className="flex flex-col gap-0.5">
                        <span className="text-[9px] uppercase text-slate-400 font-black">Daily Limit / User</span>
                        <input type="number" min="0" value={daily} onChange={(ev) => setEdit(fid, 'daily', ev.target.value)} data-testid={`ai-daily-${fid}`} className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs" placeholder="0 = unlimited" />
                      </label>
                      <label className="flex flex-col gap-0.5">
                        <span className="text-[9px] uppercase text-slate-400 font-black">Monthly Limit / User</span>
                        <input type="number" min="0" value={monthly} onChange={(ev) => setEdit(fid, 'monthly', ev.target.value)} data-testid={`ai-monthly-${fid}`} className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs" placeholder="0 = unlimited" />
                      </label>
                    </div>
                    <div className="grid grid-cols-3 gap-1 text-[10px] text-slate-400">
                      <div>Cost: <b className="text-amber-300">${f.avg_cost_per_use}</b></div>
                      <div>Today: <b className="text-cyan-300">{f.today_uses}</b></div>
                      <div>Month: <b className="text-purple-300">{f.month_uses}</b></div>
                    </div>
                    <div className="text-[10px] text-emerald-300 mt-1">₹ this month: ₹{f.est_month_cost_inr.toFixed(2)}</div>
                    {dirty && (
                      <button onClick={() => save(fid)} data-testid={`ai-save-${fid}`} className="mt-2 w-full px-2 py-1.5 rounded bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-xs font-black flex items-center justify-center gap-1"><Save className="w-3 h-3" /> Save Changes</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Top users */}
        {summary && summary.top_users?.length > 0 && (
          <div className="rounded-2xl border border-red-400/40 bg-red-500/5 p-4">
            <div className="text-[10px] uppercase tracking-widest text-red-300 font-black mb-2 flex items-center gap-1"><UsersIcon className="w-3 h-3" /> Top 10 Users by AI Spend (this month)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
              {summary.top_users.map((u, i) => (
                <div key={u._id} className="rounded-lg bg-red-500/10 border border-red-400/30 px-2 py-1.5 text-[11px]">
                  <div className="font-black text-red-300">#{i + 1} · {u.uses} calls</div>
                  <div className="text-white truncate">{u.user_info?.name || u.user_info?.username || u._id.slice(0, 12)}</div>
                  <div className="text-amber-300 font-mono">${u.cost.toFixed(4)} · ₹{(u.cost * 83).toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 🎁 Admin Grant Free Credits Dashboard */}
        <GrantFreeCreditsPanel />

        {/* 👍👎 VTON Feedback Insights — surface worst prompts for tuning */}
        <VtonFeedbackPanel />
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// VTON Feedback Panel — worst prompt combos ranked by 👎 rate
// ─────────────────────────────────────────────────────────────
function VtonFeedbackPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/trial-room/admin/vton-feedback', { params: { limit: 20 } });
      setData(r.data);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, []);
  React.useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const t = data.totals || { up: 0, down: 0 };
  const totalRatings = t.up + t.down;
  const goodPct = totalRatings > 0 ? Math.round((t.up / totalRatings) * 100) : 0;
  return (
    <div className="rounded-2xl border border-rose-400/30 bg-rose-500/5 p-4" data-testid="vton-feedback-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black">🪞 AI TRIAL ROOM · USER FEEDBACK</div>
          <h3 className="text-lg font-black">Worst-Rated VTON Combos</h3>
          <p className="text-xs text-slate-400">Which apparel × character × angle combos users flag as bad → tune prompts here.</p>
        </div>
        <button onClick={load} disabled={loading} className="px-3 py-1.5 rounded-lg bg-white/10 text-xs flex items-center gap-1" data-testid="vton-feedback-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-2">
          <div className="text-[9px] uppercase text-emerald-300 font-black">👍 Ups</div>
          <div className="text-2xl font-black text-white">{t.up}</div>
        </div>
        <div className="rounded-lg border border-rose-400/30 bg-rose-500/10 p-2">
          <div className="text-[9px] uppercase text-rose-300 font-black">👎 Downs</div>
          <div className="text-2xl font-black text-white">{t.down}</div>
        </div>
        <div className="rounded-lg border border-amber-400/30 bg-amber-500/10 p-2">
          <div className="text-[9px] uppercase text-amber-300 font-black">Good Rate</div>
          <div className="text-2xl font-black text-white">{goodPct}%</div>
        </div>
      </div>
      {data.worst_combos?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="vton-worst-combos-table">
            <thead>
              <tr className="text-left text-slate-400 border-b border-white/10">
                <th className="py-1.5 pr-2">Apparel</th>
                <th className="py-1.5 pr-2">Char</th>
                <th className="py-1.5 pr-2">Angle</th>
                <th className="py-1.5 pr-2 text-right">👎</th>
                <th className="py-1.5 pr-2 text-right">👍</th>
                <th className="py-1.5 pr-2 text-right">Bad %</th>
              </tr>
            </thead>
            <tbody>
              {data.worst_combos.map((c, i) => (
                <tr key={i} className="border-b border-white/5" data-testid={`vton-worst-row-${i}`}>
                  <td className="py-1.5 pr-2 font-mono text-fuchsia-300">{c.apparel_type || '—'}</td>
                  <td className="py-1.5 pr-2">{c.character || '—'}</td>
                  <td className="py-1.5 pr-2">{c.angle || '—'}</td>
                  <td className="py-1.5 pr-2 text-right text-rose-300 font-bold">{c.downs}</td>
                  <td className="py-1.5 pr-2 text-right text-emerald-300 font-bold">{c.ups}</td>
                  <td className="py-1.5 pr-2 text-right text-amber-300 font-black">{c.bad_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-xs text-slate-400 italic">No feedback yet. Users will rate results with 👍/👎 in Trial Room.</div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Admin — Grant Free Trial Room credits to specific users.
// ─────────────────────────────────────────────────────────────
function GrantFreeCreditsPanel() {
  const [searchQ, setSearchQ] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [grantType, setGrantType] = React.useState('bonus_trials');
  const [qty, setQty] = React.useState(5);
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [audit, setAudit] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const loadAudit = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/trial-room/admin-grants?limit=25');
      setAudit(r.data?.grants || []);
    } catch { setAudit([]); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { loadAudit(); }, [loadAudit]);

  const search = React.useCallback(async () => {
    if (searchQ.trim().length < 2) { setResults([]); return; }
    try {
      const r = await api.get(`/admin/trial-room/user-search?q=${encodeURIComponent(searchQ.trim())}&limit=10`);
      setResults(r.data?.users || []);
    } catch { setResults([]); }
  }, [searchQ]);
  React.useEffect(() => { const t = setTimeout(search, 350); return () => clearTimeout(t); }, [searchQ, search]);

  const grant = async () => {
    if (!selected) { toast.error('Select a user first'); return; }
    setBusy(true);
    try {
      await api.post('/admin/trial-room/admin-grants', {
        user_id: selected.id,
        grant_type: grantType,
        quantity: Math.max(1, Number(qty) || 1),
        note: note.trim(),
      });
      toast.success(`✅ Granted ${qty} × ${grantType.replace('_', ' ')} to @${selected.username}`);
      setSelected(null); setSearchQ(''); setNote(''); setResults([]); setQty(5);
      await loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Grant failed', { duration: 6000 });
    } finally { setBusy(false); }
  };

  const GRANT_TYPES = [
    { id: 'bonus_trials',  label: '🎁 Bonus Trials',        hint: '+N trials into user\'s pool (no expiry)' },
    { id: 'pay_per_trial', label: '⚡ Pay-per-Trial Credits', hint: '+N trials — same bucket as bonus' },
    { id: 'tester_pack',   label: '🎯 Tester Packs',         hint: 'N × (10 trials · 60d validity each)' },
    { id: 'addon_pack',    label: '💎 Add-on Packs',         hint: 'N × (60 trials · 120d validity each)' },
  ];

  return (
    <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-900/25 via-orange-900/20 to-rose-900/25 p-4" data-testid="admin-grant-credits-panel">
      <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-1">🎁 GRANT FREE TRIAL ROOM CREDITS</div>
      <h2 className="text-xl md:text-2xl font-black text-white">Manual Gift Dashboard</h2>
      <p className="text-[11px] text-slate-300 mb-3">Search a user and grant them free credits — perfect for top-3 leaderboard rewards, VIP customers, apology gifts.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Left: User picker + grant form */}
        <div className="rounded-xl border border-white/10 bg-black/30 p-3 space-y-2">
          <div>
            <div className="text-[9px] uppercase text-slate-400 font-black">Search User (name / username / email)</div>
            <input
              value={searchQ}
              onChange={(e) => { setSearchQ(e.target.value); setSelected(null); }}
              placeholder="Type at least 2 characters..."
              data-testid="admin-grant-user-search"
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm"
            />
            {results.length > 0 && !selected && (
              <div className="mt-1 max-h-40 overflow-auto rounded border border-white/10 bg-slate-900">
                {results.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => { setSelected(u); setResults([]); }}
                    data-testid={`admin-grant-user-${u.username}`}
                    className="w-full text-left px-2 py-1.5 hover:bg-white/5 text-xs flex items-center gap-2 border-b border-white/5"
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-fuchsia-500 to-amber-500 flex items-center justify-center text-[10px] font-black text-white">
                      {(u.name || u.username || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-white truncate">{u.name} <span className="text-slate-400 font-mono">@{u.username}</span></div>
                      <div className="text-[10px] text-slate-400 truncate">{u.email}{u.role === 'admin' ? ' · ADMIN' : ''}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {selected && (
              <div className="mt-1.5 rounded bg-emerald-500/15 border border-emerald-400/30 px-2 py-1.5 flex items-center justify-between text-xs" data-testid="admin-grant-selected">
                <div className="min-w-0"><b className="text-emerald-300">✓</b> {selected.name} <span className="text-slate-400 font-mono">@{selected.username}</span></div>
                <button onClick={() => setSelected(null)} className="text-red-300 text-xs font-black">✕</button>
              </div>
            )}
          </div>

          <div>
            <div className="text-[9px] uppercase text-slate-400 font-black">Grant Type</div>
            <select
              value={grantType}
              onChange={(e) => setGrantType(e.target.value)}
              data-testid="admin-grant-type"
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm"
            >
              {GRANT_TYPES.map((g) => <option key={g.id} value={g.id} className="bg-slate-900">{g.label}</option>)}
            </select>
            <div className="text-[10px] text-slate-400 mt-0.5">{GRANT_TYPES.find((g) => g.id === grantType)?.hint}</div>
          </div>

          <div>
            <div className="text-[9px] uppercase text-slate-400 font-black">Quantity</div>
            <input
              type="number" min="1" max="100" value={qty}
              onChange={(e) => setQty(e.target.value)}
              data-testid="admin-grant-qty"
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm"
            />
          </div>

          <div>
            <div className="text-[9px] uppercase text-slate-400 font-black">Note (audit reason)</div>
            <input
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Top-3 leaderboard reward · VIP · apology"
              data-testid="admin-grant-note"
              maxLength={280}
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm"
            />
          </div>

          <button
            onClick={grant}
            disabled={busy || !selected}
            data-testid="admin-grant-submit"
            className="w-full px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-black text-sm font-black disabled:opacity-40"
          >
            {busy ? '⏳ Granting...' : '🎁 Grant Credits'}
          </button>
        </div>

        {/* Right: Audit log */}
        <div className="rounded-xl border border-white/10 bg-black/30 p-3" data-testid="admin-grant-audit">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">📜 Recent Grants (Audit Log)</div>
            <button onClick={loadAudit} className="text-[10px] text-slate-400 hover:text-white">↻ Refresh</button>
          </div>
          {loading ? (
            <div className="text-slate-400 text-xs text-center py-4">Loading...</div>
          ) : audit.length === 0 ? (
            <div className="text-slate-400 text-xs text-center py-4">No grants yet.</div>
          ) : (
            <div className="max-h-80 overflow-auto space-y-1.5">
              {audit.map((g) => (
                <div key={g.id} className="rounded bg-white/5 border border-white/10 px-2 py-1.5 text-[11px]" data-testid={`audit-row-${g.id}`}>
                  <div className="font-black text-white truncate">
                    {g.quantity}× {g.grant_type.replace('_', ' ')} → <span className="text-emerald-300">@{g.target_username}</span>
                  </div>
                  <div className="text-[10px] text-slate-400">
                    by @{g.admin_username} · {new Date(g.created_at).toLocaleString('en-IN')}
                  </div>
                  {g.note && <div className="text-[10px] text-amber-200 italic truncate">💬 {g.note}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// 💰 Trial Room AI Cache — Cost Savings Tile
function CostSavingsTile() {
  const [d, setD] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/trial-room/cache');
      setD(r.data);
    } catch (e) { /* silent — admin panel resilience */ }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const hitRate = Number(d.cache_hit_rate_pct || 0);
  const barColor = hitRate >= 40 ? 'bg-emerald-400' : hitRate >= 15 ? 'bg-amber-400' : 'bg-rose-400';
  return (
    <div data-testid="ai-cost-savings-tile" className="rounded-2xl border-2 border-emerald-400/40 bg-gradient-to-br from-emerald-950/60 via-slate-900 to-teal-950/60 p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">💰 TRIAL ROOM · AI COST SAVINGS</div>
          <div className="text-sm text-slate-300">pHash cache + 1024px downscale — dedup near-duplicate uploads</div>
        </div>
        <button data-testid="ai-cost-refresh" onClick={load} disabled={loading} className="px-2.5 py-1 rounded-lg bg-white/10 text-[11px] flex items-center gap-1"><RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-3" data-testid="tile-hit-rate">
          <div className="text-[9px] uppercase tracking-widest text-emerald-300 font-black">Cache Hit Rate</div>
          <div className="text-3xl font-black text-white">{hitRate.toFixed(1)}%</div>
          <div className="mt-2 h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
            <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, hitRate)}%` }} />
          </div>
        </div>
        <div className="rounded-xl border border-cyan-400/40 bg-cyan-500/10 p-3" data-testid="tile-month-hits">
          <div className="text-[9px] uppercase tracking-widest text-cyan-300 font-black">This Month · Cache Hits</div>
          <div className="text-3xl font-black text-white">{fmt(d.month_cache_hits)}</div>
          <div className="text-[10px] text-cyan-200/70">of {fmt(d.total_cache_hits)} lifetime</div>
        </div>
        <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-3" data-testid="tile-bytes-saved">
          <div className="text-[9px] uppercase tracking-widest text-amber-300 font-black">Bytes Saved · This Month</div>
          <div className="text-3xl font-black text-white">{fmt(d.month_bytes_saved_mb)} <span className="text-lg">MB</span></div>
          <div className="text-[10px] text-amber-200/70">{fmt(d.bytes_saved_mb)} MB lifetime</div>
        </div>
        <div className="rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/10 p-3" data-testid="tile-inr-saved">
          <div className="text-[9px] uppercase tracking-widest text-fuchsia-300 font-black">INR Saved · This Month</div>
          <div className="text-3xl font-black text-white">₹{fmt(d.month_inr_saved)}</div>
          <div className="text-[10px] text-fuchsia-200/70">₹{fmt(d.estimated_inr_saved)} lifetime · @ ₹{d.cost_per_call_inr}/call</div>
        </div>
      </div>
    </div>
  );
}
